import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Download, RefreshCw, Search, AlertCircle, ChevronDown, ChevronRight, Activity } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/Tabs';
import { api } from '../../lib/api';

interface LogEvent {
  timestamp: number;
  message: string;
  logStreamName?: string;
  requestId?: string;
}

interface LogGroup {
  requestId: string;
  startTime: number;
  endTime: number;
  logs: LogEvent[];
  expanded: boolean;
}

interface LogsResponse {
  logs: LogEvent[];
  count: number;
  uniqueRequests?: number;
  pagesRetrieved?: number;
  startTime: string;
  endTime: string;
  environment: string;
  hours?: number;
}

interface ExecutionEvent {
  id?: number;
  previousEventId?: number;
  type?: string;
  timestamp?: string;
  eventTimestamp?: string;
  stateEnteredEventDetails?: {
    name?: string;
    input?: string;
  };
  stateExitedEventDetails?: {
    name?: string;
    output?: string;
  };
  taskFailedEventDetails?: {
    error?: string;
    cause?: string;
    resource?: string;
    resourceType?: string;
  };
  lambdaFunctionFailedEventDetails?: {
    error?: string;
    cause?: string;
  };
  taskScheduledEventDetails?: {
    resource?: string;
    resourceType?: string;
  };
  lambdaFunctionScheduledEventDetails?: {
    resource?: string;
  };
  [key: string]: unknown;
}

interface ExecutionHistoryResponse {
  execution?: {
    executionArn?: string;
    status?: string;
    startDate?: string;
    stopDate?: string;
    input?: string;
    output?: string;
  };
  events?: ExecutionEvent[];
  environment?: string;
  nextToken?: string;
}

interface ExecutionSummary {
  executionArn: string;
  name?: string;
  status?: string;
  startDate?: string;
  stopDate?: string;
}

interface ExecutionListResponse {
  executions?: ExecutionSummary[];
  environment?: string;
  nextToken?: string;
}

interface WorkflowLogEvent {
  timestamp: number;
  message: string;
  logStreamName?: string;
  logGroupName?: string;
}

interface WorkflowLogsApiResponse {
  environment?: string;
  executionArn?: string;
  correlationId?: string;
  startTime?: string;
  endTime?: string;
  lambdaLogGroups?: string[];
  lambdaLogs?: WorkflowLogEvent[];
  stepFunctionLogGroup?: string | null;
  stepFunctionLogs?: WorkflowLogEvent[];
}

const MAX_HISTORY_PAGES = 10;

type LogType = 'trader' | 'executions';

type StateStatus =
  | 'passed'
  | 'failed'
  | 'skipped'
  | 'in-progress'
  | 'pending'
  | 'completed'
  | 'cancelled'
  | 'active'
  | 'submitted'
  | 'timeout';

interface StateSummary {
  id: string;
  name: string;
  status: StateStatus;
  events: ExecutionEvent[];
  inputRaw?: string;
  inputJson?: unknown;
  outputRaw?: string;
  outputJson?: unknown;
  error?: string;
  cause?: string;
  startedAt?: string;
  endedAt?: string;
}

const safeParseJson = (value?: string): unknown | undefined => {
  if (!value) {
    return undefined;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    console.debug('Failed to parse JSON payload', { error, valueSnippet: value.slice(0, 200) });
    return undefined;
  }
};

const extractCorrelationId = (executionInput?: string): string | undefined => {
  const parsed = safeParseJson(executionInput);
  if (parsed && typeof parsed === 'object' && 'correlationId' in parsed) {
    const candidate = (parsed as { correlationId?: unknown }).correlationId;
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate;
    }
  }
  return undefined;
};

const parseTimestampMs = (value?: string): number | undefined => {
  if (!value) {
    return undefined;
  }
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? undefined : ms;
};

const getTimestampMs = (event: ExecutionEvent): number => {
  const raw = event.timestamp ?? event.eventTimestamp;
  if (!raw) {
    return 0;
  }
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? 0 : ms;
};

const inferStatusFromOutput = (summaryName: string, output: unknown): StateStatus | undefined => {
  if (!output || typeof output !== 'object') {
    return undefined;
  }

  const record = output as Record<string, unknown>;
  const values: string[] = [];

  const push = (value: unknown) => {
    if (typeof value === 'string' && value.trim().length > 0) {
      values.push(value);
    }
  };

  push(record.status);
  push(record.decision);
  push(record.reason);

  const safety = record.safety as Record<string, unknown> | undefined;
  if (safety) {
    push(safety.status);
    push(safety.decision);
    push(safety.reason);
  }

  const order = record.order as Record<string, unknown> | undefined;
  if (order) {
    push(order.status);
  }

  const trailingStop = record.trailingStop as Record<string, unknown> | undefined;
  if (trailingStop) {
    push(trailingStop.status);
    const nextAction = trailingStop.nextAction as Record<string, unknown> | undefined;
    push(nextAction?.reason);
    if (nextAction?.continue === false) {
      push('completed');
    }
  }

  const autoCancel = record.autoCancel as Record<string, unknown> | undefined;
  if (autoCancel) {
    push(autoCancel.status);
    const nextAction = autoCancel.nextAction as Record<string, unknown> | undefined;
    push(nextAction?.reason);
    if (nextAction?.continue === false) {
      push('completed');
    }
  }

  const normalise = (value: string): StateStatus | undefined => {
    const token = value.trim().toLowerCase();
    if (!token) {
      return undefined;
    }
    if (token.includes('skip') || token === 'disabled') {
      return 'skipped';
    }
    if (token === 'pending' || token === 'awaiting' || token === 'waiting') {
      return 'pending';
    }
    if (token === 'completed' || token === 'complete' || token === 'done' || token === 'finished' || token === 'success') {
      return 'completed';
    }
    if (token === 'cancelled' || token === 'canceled' || token === 'aborted' || token === 'stopped') {
      return 'cancelled';
    }
    if (token === 'active' || token === 'running' || token === 'monitoring') {
      return 'active';
    }
    if (token === 'submitted' || token === 'placed' || token === 'enqueued') {
      return 'submitted';
    }
    if (token === 'timeout' || token === 'timed_out' || token === 'timed out' || token === 'timedout') {
      return 'timeout';
    }
    if (token === 'failed' || token === 'error' || token === 'failure' || token === 'rejected') {
      return 'failed';
    }
    if (token === 'passed' || token === 'pass' || token === 'succeeded') {
      return 'passed';
    }
    return undefined;
  };

  for (const value of values) {
    const status = normalise(value);
    if (status) {
      return status;
    }
  }

  const nameToken = summaryName.toLowerCase();
  if (nameToken.includes('trailingstop')) {
    const trailingStatus = (record.trailingStop as Record<string, unknown> | undefined)?.status;
    if (typeof trailingStatus === 'string') {
      const status = normalise(trailingStatus);
      if (status) {
        return status;
      }
    }
  }

  if (nameToken.includes('autocancel')) {
    const autoStatus = (record.autoCancel as Record<string, unknown> | undefined)?.status;
    if (typeof autoStatus === 'string') {
      const status = normalise(autoStatus);
      if (status) {
        return status;
      }
    }
  }

  return undefined;
};

const deriveStateSummaries = (events?: ExecutionEvent[]): StateSummary[] => {
  if (!events?.length) {
    return [];
  }

  const sortedEvents = [...events].sort((a, b) => getTimestampMs(a) - getTimestampMs(b));
  const eventsById = new Map<number, ExecutionEvent>();
  sortedEvents.forEach((event) => {
    if (typeof event.id === 'number') {
      eventsById.set(event.id, event);
    }
  });

  const summaries: StateSummary[] = [];
  const entryMap = new Map<number, StateSummary>();
  const skippedFailureErrors = new Set(['PositionSafetyBlocked']);

  const classifyFailEventOutcome = (
    event: ExecutionEvent,
  ): { status?: StateStatus; error?: string; cause?: string } => {
    const result: { status?: StateStatus; error?: string; cause?: string } = {};
    const rawInput = event.stateEnteredEventDetails?.input;
    if (typeof rawInput !== 'string') {
      return result;
    }

    const parsed = safeParseJson(rawInput);
    if (!parsed || typeof parsed !== 'object') {
      return result;
    }

    const record = parsed as Record<string, unknown>;
    const failure = record.failure;
    const failureRecord =
      failure && typeof failure === 'object' && failure !== null
        ? (failure as Record<string, unknown>)
        : undefined;

    const errorCandidate =
      typeof failureRecord?.error === 'string'
        ? failureRecord.error
        : typeof record.error === 'string'
          ? record.error
          : undefined;
    if (errorCandidate) {
      result.error = errorCandidate;
      if (skippedFailureErrors.has(errorCandidate)) {
        result.status = 'skipped';
      }
    }

    const causeCandidate =
      typeof failureRecord?.cause === 'string'
        ? failureRecord.cause
        : typeof record.cause === 'string'
          ? record.cause
          : undefined;
    if (causeCandidate) {
      result.cause = causeCandidate;
    }

    const statusCandidates: string[] = [];
    if (typeof failureRecord?.status === 'string') {
      statusCandidates.push(failureRecord.status);
    }
    if (typeof record.status === 'string') {
      statusCandidates.push(record.status);
    }

    for (const candidate of statusCandidates) {
      if (candidate.toLowerCase().includes('skip')) {
        result.status = 'skipped';
        break;
      }
    }

    return result;
  };

  const findActiveSummary = (name: string): StateSummary | undefined => {
    for (let i = summaries.length - 1; i >= 0; i -= 1) {
      const summary = summaries[i];
      if (summary.name === name && summary.status === 'in-progress') {
        return summary;
      }
    }
    return undefined;
  };

  const resolveSummaryFromEvent = (event: ExecutionEvent): StateSummary | undefined => {
    const visited = new Set<number>();
    let cursor: ExecutionEvent | undefined = event;
    while (cursor) {
      if (typeof cursor.id === 'number' && entryMap.has(cursor.id)) {
        return entryMap.get(cursor.id);
      }
      const prevId = cursor.previousEventId;
      if (typeof prevId === 'number' && !visited.has(prevId)) {
        visited.add(prevId);
        cursor = eventsById.get(prevId);
      } else {
        break;
      }
    }
    return undefined;
  };

  const createSummary = (name: string, event: ExecutionEvent): StateSummary => {
    const summary: StateSummary = {
      id: `${name || 'State'}-${summaries.length + 1}`,
      name: name || 'Unnamed state',
      status: 'in-progress',
      events: [],
      inputRaw: event.stateEnteredEventDetails?.input,
      inputJson: safeParseJson(event.stateEnteredEventDetails?.input),
      startedAt: event.timestamp ?? event.eventTimestamp,
    };
    summaries.push(summary);
    if (typeof event.id === 'number') {
      entryMap.set(event.id, summary);
    }
    return summary;
  };

  const markSummaryAsFailed = (summary: StateSummary, event: ExecutionEvent, error?: string, cause?: string) => {
    if (summary.status !== 'failed') {
      summary.status = 'failed';
    }
    summary.error = error ?? summary.error;
    summary.cause = cause ?? summary.cause;
    summary.endedAt = summary.endedAt ?? (event.timestamp ?? event.eventTimestamp);
    summary.events.push(event);
  };

  sortedEvents.forEach((event) => {
    const eventType = event.type ?? '';

    if (eventType.endsWith('StateEntered')) {
      const name = event.stateEnteredEventDetails?.name;
      const summary = createSummary(name ?? eventType.replace('StateEntered', ''), event);
      summary.events.push(event);
      return;
    }

    if (eventType.endsWith('StateExited')) {
      const name = event.stateExitedEventDetails?.name;
      if (!name) {
        return;
      }
      const summary = findActiveSummary(name) ?? createSummary(name, event);
      summary.status = summary.status === 'failed' ? summary.status : 'passed';
      summary.outputRaw = event.stateExitedEventDetails?.output ?? summary.outputRaw;
      summary.outputJson = summary.outputJson ?? safeParseJson(event.stateExitedEventDetails?.output);
      if (summary.status !== 'failed') {
        const inferred = inferStatusFromOutput(summary.name, summary.outputJson);
        if (inferred) {
          summary.status = inferred;
        }
      }
      summary.endedAt = event.timestamp ?? event.eventTimestamp ?? summary.endedAt;
      summary.events.push(event);
      return;
    }

    if (eventType === 'TaskFailed' || eventType === 'TaskStateFailed' || eventType === 'ParallelStateFailed' || eventType === 'MapStateFailed') {
      const summary = resolveSummaryFromEvent(event);
      if (summary) {
        markSummaryAsFailed(summary, event, event.taskFailedEventDetails?.error, event.taskFailedEventDetails?.cause);
      }
      return;
    }

    if (eventType === 'LambdaFunctionFailed') {
      const summary = resolveSummaryFromEvent(event);
      if (summary) {
        markSummaryAsFailed(summary, event, event.lambdaFunctionFailedEventDetails?.error, event.lambdaFunctionFailedEventDetails?.cause);
      }
      return;
    }

    if (eventType === 'FailStateEntered') {
      const name = event.stateEnteredEventDetails?.name ?? 'FailState';
      const summary = findActiveSummary(name) ?? createSummary(name, event);
      const classification = classifyFailEventOutcome(event);
      const resolvedCause =
        classification.cause ?? (typeof event.stateEnteredEventDetails?.input === 'string'
          ? event.stateEnteredEventDetails.input
          : undefined);

      if (classification.status && classification.status !== 'failed') {
        summary.status = classification.status;
        if (classification.error && !summary.error) {
          summary.error = classification.error;
        }
        if (resolvedCause && !summary.cause) {
          summary.cause = resolvedCause;
        }
        summary.endedAt = summary.endedAt ?? (event.timestamp ?? event.eventTimestamp);
        summary.events.push(event);
        return;
      }

      markSummaryAsFailed(summary, event, classification.error ?? 'State failed', resolvedCause);
      return;
    }

    if (eventType === 'ExecutionFailed') {
      const summary = summaries[summaries.length - 1];
      if (summary) {
        if (summary.status === 'skipped') {
          summary.endedAt = summary.endedAt ?? (event.timestamp ?? event.eventTimestamp);
          summary.events.push(event);
        } else {
          markSummaryAsFailed(summary, event, 'Execution failed', undefined);
        }
      }
      return;
    }

    if (eventType === 'SucceedStateEntered') {
      const name = event.stateEnteredEventDetails?.name ?? 'Success';
      const summary = findActiveSummary(name) ?? createSummary(name, event);
      summary.status = 'passed';
      summary.endedAt = event.timestamp ?? event.eventTimestamp ?? summary.endedAt;
      summary.events.push(event);
      return;
    }

    // For other events, try to attach them to the currently active state for richer context.
    const summary = resolveSummaryFromEvent(event);
    if (summary) {
      summary.events.push(event);
    }
  });

  return summaries;
};

const formatDateTime = (value?: string): string => {
  if (!value) {
    return 'n/a';
  }
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) {
    return value;
  }
  return new Date(ms).toLocaleString();
};

const formatStructuredText = (raw?: string, parsed?: unknown): string | undefined => {
  if (parsed !== undefined) {
    try {
      return JSON.stringify(parsed, null, 2);
    } catch (error) {
      console.debug('Failed to stringify structured payload', { error });
    }
  }
  return raw;
};

const statusBadgeClass = (status: StateStatus): string => {
  switch (status) {
    case 'passed':
    case 'completed':
      return 'bg-green-100 text-green-700 border border-green-200';
    case 'failed':
      return 'bg-red-100 text-red-700 border border-red-200';
    case 'skipped':
      return 'bg-gray-100 text-gray-700 border border-gray-200';
    case 'pending':
      return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
    case 'cancelled':
      return 'bg-gray-100 text-gray-600 border border-gray-200';
    case 'active':
      return 'bg-blue-100 text-blue-700 border border-blue-200';
    case 'submitted':
      return 'bg-indigo-100 text-indigo-700 border border-indigo-200';
    case 'timeout':
      return 'bg-orange-100 text-orange-700 border border-orange-200';
    default:
      return 'bg-blue-100 text-blue-700 border border-blue-200';
  }
};

const statusLabel = (status: StateStatus): string => {
  switch (status) {
    case 'passed':
      return 'Passed';
    case 'failed':
      return 'Failed';
    case 'skipped':
      return 'Skipped';
    case 'pending':
      return 'Pending';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    case 'active':
      return 'Active';
    case 'submitted':
      return 'Submitted';
    case 'timeout':
      return 'Timed Out';
    default:
      return 'In Progress';
  }
};

const lambdaArnToLogGroup = (arn?: string): string | undefined => {
  if (!arn || typeof arn !== 'string') {
    return undefined;
  }
  const [, functionNamePart] = arn.split(':function:');
  if (!functionNamePart) {
    return undefined;
  }
  const functionName = functionNamePart.split(':')[0];
  if (!functionName) {
    return undefined;
  }
  return `/aws/lambda/${functionName}`;
};

const deriveSummaryLogGroups = (summary: StateSummary): string[] => {
  const groups = new Set<string>();
  summary.events.forEach((event) => {
    const taskGroup = lambdaArnToLogGroup(event.taskScheduledEventDetails?.resource);
    if (taskGroup) {
      groups.add(taskGroup);
    }
    const lambdaGroup = lambdaArnToLogGroup(event.lambdaFunctionScheduledEventDetails?.resource);
    if (lambdaGroup) {
      groups.add(lambdaGroup);
    }
  });
  return Array.from(groups);
};

const filterLogsForSummaryWindow = (logs: WorkflowLogEvent[], summary: StateSummary): WorkflowLogEvent[] => {
  if (!summary.startedAt && !summary.endedAt) {
    return logs;
  }

  const startMs = parseTimestampMs(summary.startedAt);
  const endMs = parseTimestampMs(summary.endedAt);
  const toleranceMs = 5 * 60 * 1000; // five-minute buffer to account for clock skew and async retries

  const windowed = logs.filter((log) => {
    const timestamp = log.timestamp ?? 0;
    if (startMs !== undefined && timestamp < startMs - toleranceMs) {
      return false;
    }
    if (endMs !== undefined && timestamp > endMs + toleranceMs) {
      return false;
    }
    return true;
  });

  return windowed.length > 0 ? windowed : logs;
};

export const LogsViewer: React.FC = () => {
  const [activeTab, setActiveTab] = useState<LogType>('trader');

  // Separate state for each tab
  const [traderLogs, setTraderLogs] = useState<LogEvent[]>([]);
  const [traderLogGroups, setTraderLogGroups] = useState<LogGroup[]>([]);
  const [traderFilteredGroups, setTraderFilteredGroups] = useState<LogGroup[]>([]);
  const [traderMetadata, setTraderMetadata] = useState<Omit<LogsResponse, 'logs'> | null>(null);

  const [executionList, setExecutionList] = useState<ExecutionSummary[]>([]);
  const [executionListLoading, setExecutionListLoading] = useState(false);
  const [executionListError, setExecutionListError] = useState<string | null>(null);
  const [executionListNextToken, setExecutionListNextToken] = useState<string | undefined>(undefined);
  const [executionEnvironment, setExecutionEnvironment] = useState<string | undefined>(undefined);
  const [selectedExecutionArn, setSelectedExecutionArn] = useState<string | null>(null);
  const [executionDetail, setExecutionDetail] = useState<ExecutionHistoryResponse | null>(null);
  const [executionDetailLoading, setExecutionDetailLoading] = useState(false);
  const [executionDetailError, setExecutionDetailError] = useState<string | null>(null);
  const [workflowLogs, setWorkflowLogs] = useState<WorkflowLogsApiResponse | null>(null);
  const [workflowLogsLoading, setWorkflowLogsLoading] = useState(false);
  const [workflowLogsError, setWorkflowLogsError] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const sortedExecutionEvents = useMemo(() => {
    if (!executionDetail?.events) {
      return [] as ExecutionEvent[];
    }
    return [...executionDetail.events].sort((a, b) => getTimestampMs(a) - getTimestampMs(b));
  }, [executionDetail?.events]);

  const stateSummaries = useMemo(() => deriveStateSummaries(sortedExecutionEvents), [sortedExecutionEvents]);

  const lambdaLogsByGroup = useMemo(() => {
    const map: Record<string, WorkflowLogEvent[]> = {};
    if (!workflowLogs?.lambdaLogs) {
      return map;
    }
    workflowLogs.lambdaLogs.forEach((event) => {
      if (!event.logGroupName) {
        return;
      }
      if (!map[event.logGroupName]) {
        map[event.logGroupName] = [];
      }
      map[event.logGroupName].push(event);
    });
    Object.values(map).forEach((events) => {
      events.sort((a, b) => a.timestamp - b.timestamp);
    });
    return map;
  }, [workflowLogs?.lambdaLogs]);

  const stepFunctionLogs = useMemo(() => workflowLogs?.stepFunctionLogs ?? [], [workflowLogs?.stepFunctionLogs]);

  const previousExecutionKeyRef = useRef<string | null>(null);
  const [expandedSummaries, setExpandedSummaries] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const executionKey = executionDetail?.execution?.executionArn ?? null;
    if (previousExecutionKeyRef.current !== executionKey) {
      previousExecutionKeyRef.current = executionKey;
      const defaults: Record<string, boolean> = {};
      stateSummaries.forEach((summary) => {
        defaults[summary.id] = false;
      });
      setExpandedSummaries(defaults);
      return;
    }

    setExpandedSummaries((prev) => {
      const next = { ...prev };
      let changed = false;
      stateSummaries.forEach((summary) => {
        if (!(summary.id in next)) {
          next[summary.id] = false;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [stateSummaries, executionDetail?.execution?.executionArn]);

  const toggleSummary = useCallback((id: string) => {
    setExpandedSummaries((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // Get current tab's data

  useEffect(() => {
    if (activeTab === 'trader') {
      fetchLogs('trader');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);


  useEffect(() => {
    if (activeTab !== 'trader') {
      return;
    }

    if (searchTerm) {
      const filtered = traderLogGroups.filter(group =>
        group.logs.some(log => log.message.toLowerCase().includes(searchTerm.toLowerCase()))
      ).map(group => ({
        ...group,
        logs: group.logs.filter(log =>
          log.message.toLowerCase().includes(searchTerm.toLowerCase())
        )
      }));
      setTraderFilteredGroups(filtered);
    } else {
      setTraderFilteredGroups(traderLogGroups);
    }
  }, [searchTerm, traderLogGroups, activeTab]);

  const extractRequestId = (message: string): string | undefined => {
    // Try to extract RequestId from various log formats
    const patterns = [
      /RequestId:\s*([a-f0-9-]+)/i,
      /START RequestId:\s*([a-f0-9-]+)/,
      /END RequestId:\s*([a-f0-9-]+)/,
      /REPORT RequestId:\s*([a-f0-9-]+)/,
      // Also try to extract from the log timestamp format
      /^[0-9-T:.Z]+\s+([a-f0-9-]+)\s+/,
      // CloudWatch format: "2024-01-01T12:00:00.000Z [request-id] message"
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z\s+([a-f0-9-]+)\s+/
    ];

    for (const pattern of patterns) {
      const match = message.match(pattern);
      if (match) return match[1];
    }

    return undefined;
  };

  const groupLogsByRequestId = (logEvents: LogEvent[]): LogGroup[] => {
    const groups: { [key: string]: LogGroup } = {};

    logEvents.forEach(log => {
      const requestId = extractRequestId(log.message) || log.requestId || 'ungrouped';

      if (!groups[requestId]) {
        groups[requestId] = {
          requestId,
          startTime: log.timestamp,
          endTime: log.timestamp,
          logs: [],
          expanded: false
        };
      }

      groups[requestId].logs.push(log);
      groups[requestId].endTime = Math.max(groups[requestId].endTime, log.timestamp);
      groups[requestId].startTime = Math.min(groups[requestId].startTime, log.timestamp);
    });

    // Sort logs within each group by timestamp (ascending order)
    Object.values(groups).forEach(group => {
      group.logs.sort((a, b) => a.timestamp - b.timestamp);
    });

    // Sort groups by start time (newest first)
    return Object.values(groups).sort((a, b) => b.startTime - a.startTime);
  };

  const fetchExecutionList = useCallback(
    async ({ reset = false, nextToken }: { reset?: boolean; nextToken?: string } = {}) => {
      if (reset) {
        setExecutionList([]);
        setExecutionListNextToken(undefined);
        setExecutionEnvironment(undefined);
        setSelectedExecutionArn(null);
        setExecutionDetail(null);
        setExecutionDetailError(null);
        setWorkflowLogs(null);
        setWorkflowLogsError(null);
        setWorkflowLogsLoading(false);
      }

      setExecutionListLoading(true);
      setExecutionListError(null);

      try {
        const params: Record<string, string> = { maxResults: '25' };
        if (nextToken) {
          params.nextToken = nextToken;
        }

        const response = await api.get<ExecutionListResponse>('/executions', { params });
        setExecutionEnvironment(response.data.environment);
        setExecutionList(prev => {
          const incoming = response.data.executions ?? [];
          if (reset || prev.length === 0) {
            return incoming;
          }

          const seen = new Set(prev.map(execution => execution.executionArn));
          const merged = [...prev];
          incoming.forEach(execution => {
            if (!seen.has(execution.executionArn)) {
              merged.push(execution);
            }
          });
          return merged;
        });
        setExecutionListNextToken(response.data.nextToken);
      } catch (err) {
        setExecutionListError(err instanceof Error ? err.message : 'Failed to fetch Step Functions executions');
      } finally {
        setExecutionListLoading(false);
      }
    },
    [],
  );

  const fetchExecutionDetail = useCallback(
    async (executionArn: string, options: { nextToken?: string; append?: boolean } = {}) => {
      if (!executionArn) {
        setExecutionDetail(null);
        return;
      }

      const { nextToken, append } = options;

      setExecutionDetailLoading(true);
      setExecutionDetailError(null);

      try {
        const params: Record<string, string> = { executionArn };

        if (append) {
          if (nextToken) {
            params.nextToken = nextToken;
          }

          const response = await api.get<ExecutionHistoryResponse>('/executions', { params });

          setExecutionDetail(prev => {
            const previous = prev ?? {};
            const existingEvents = previous.events ?? [];
            const incomingEvents = response.data.events ?? [];

            return {
              ...previous,
              ...response.data,
              execution: response.data.execution ?? previous.execution,
              environment: response.data.environment ?? previous.environment,
              events: [...existingEvents, ...incomingEvents],
            };
          });
        } else {
          const aggregatedEvents: ExecutionEvent[] = [];
          let aggregateResponse: ExecutionHistoryResponse | null = null;
          let cursor = nextToken;
          let pagesFetched = 0;

          do {
            const pageParams = { ...params };
            if (cursor) {
              pageParams.nextToken = cursor;
            }

            const pageResponse = await api.get<ExecutionHistoryResponse>('/executions', { params: pageParams });

            if (!aggregateResponse) {
              aggregateResponse = { ...pageResponse.data };
            } else {
              const current: ExecutionHistoryResponse = aggregateResponse;
              aggregateResponse = {
                ...current,
                execution: current.execution ?? pageResponse.data.execution,
                environment: current.environment ?? pageResponse.data.environment,
              };
            }

            aggregatedEvents.push(...(pageResponse.data.events ?? []));
            cursor = pageResponse.data.nextToken;
            pagesFetched += 1;
          } while (cursor && pagesFetched < MAX_HISTORY_PAGES);

          if (aggregateResponse) {
            const mergedResponse: ExecutionHistoryResponse = {
              execution: aggregateResponse.execution,
              environment: aggregateResponse.environment,
              events: aggregatedEvents,
              nextToken: cursor,
            };
            setExecutionDetail(mergedResponse);
          } else {
            setExecutionDetail(null);
          }
        }
      } catch (err) {
        console.error('Failed to fetch execution history', err);
        setExecutionDetailError(err instanceof Error ? err.message : 'Failed to fetch execution history');
        if (!append) {
          setExecutionDetail(null);
        }
      } finally {
        setExecutionDetailLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (activeTab !== 'executions') {
      return;
    }

    fetchExecutionList({ reset: true });
  }, [activeTab, fetchExecutionList]);

  useEffect(() => {
    if (activeTab !== 'executions') {
      return;
    }

    if (!selectedExecutionArn && executionList.length > 0) {
      setSelectedExecutionArn(executionList[0].executionArn);
    }
  }, [executionList, selectedExecutionArn, activeTab]);

  useEffect(() => {
    if (activeTab !== 'executions' || !selectedExecutionArn) {
      return;
    }

    fetchExecutionDetail(selectedExecutionArn);
  }, [selectedExecutionArn, activeTab, fetchExecutionDetail]);

  useEffect(() => {
    if (activeTab !== 'executions') {
      return;
    }

    const executionArn = executionDetail?.execution?.executionArn;
    if (!executionArn) {
      setWorkflowLogs(null);
      setWorkflowLogsError(null);
      setWorkflowLogsLoading(false);
      return;
    }

    const correlationId = extractCorrelationId(executionDetail?.execution?.input);

    let cancelled = false;

    const params: Record<string, string> = {
      logType: 'workflow',
      executionArn,
    };

    if (correlationId) {
      params.correlationId = correlationId;
    }

    if (executionDetail?.execution?.startDate) {
      params.startTime = executionDetail.execution.startDate;
    }

    params.endTime = executionDetail?.execution?.stopDate ?? new Date().toISOString();

    const loadLogs = async () => {
      setWorkflowLogsLoading(true);
      setWorkflowLogsError(null);
      try {
        const response = await api.get<WorkflowLogsApiResponse>('/logs', {
          params,
        });
        if (!cancelled) {
          setWorkflowLogs(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          setWorkflowLogs(null);
          setWorkflowLogsError(err instanceof Error ? err.message : 'Failed to load workflow logs');
        }
      } finally {
        if (!cancelled) {
          setWorkflowLogsLoading(false);
        }
      }
    };

    loadLogs();

    return () => {
      cancelled = true;
    };
  }, [activeTab, executionDetail?.execution?.executionArn, executionDetail?.execution?.startDate, executionDetail?.execution?.stopDate, executionDetail?.execution?.input]);

  const fetchLogs = async (logType: LogType) => {
    setLoading(true);
    setError(null);
    try {
      if (logType !== 'trader') {
        return;
      }

      const response = await api.get<LogsResponse>('/logs', {
        params: { logType }
      });

      const enhancedLogs = response.data.logs.map(log => ({
        ...log,
        requestId: extractRequestId(log.message)
      }));

      const groupsWithExpansion = groupLogsByRequestId(enhancedLogs).map(group => ({
        ...group,
        expanded: false
      }));

      setTraderLogs(enhancedLogs);
      setTraderLogGroups(groupsWithExpansion);
      setTraderFilteredGroups(groupsWithExpansion);
      setTraderMetadata({
        count: response.data.count,
        startTime: response.data.startTime,
        endTime: response.data.endTime,
        environment: response.data.environment,
        hours: response.data.hours,
        uniqueRequests: response.data.uniqueRequests,
        pagesRetrieved: response.data.pagesRetrieved
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch logs');
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectExecution = useCallback(
    (executionArn: string) => {
      setSelectedExecutionArn(executionArn);
      setExecutionDetail(null);
      setWorkflowLogs(null);
      setWorkflowLogsError(null);
      setExecutionDetailError(null);
    },
    [],
  );

  const handleLoadMoreExecutions = useCallback(() => {
    if (!executionListNextToken) {
      return;
    }
    fetchExecutionList({ nextToken: executionListNextToken });
  }, [executionListNextToken, fetchExecutionList]);

  const handleLoadMoreEvents = useCallback(() => {
    if (!executionDetail?.nextToken || !selectedExecutionArn) {
      return;
    }
    fetchExecutionDetail(selectedExecutionArn, {
      nextToken: executionDetail.nextToken,
      append: true,
    });
  }, [executionDetail?.nextToken, selectedExecutionArn, fetchExecutionDetail]);

  const toggleGroup = (requestId: string) => {
    if (activeTab !== 'trader') {
      return;
    }

    setTraderLogGroups(prev => prev.map(group =>
      group.requestId === requestId
        ? { ...group, expanded: !group.expanded }
        : group
    ));
    setTraderFilteredGroups(prev => prev.map(group =>
      group.requestId === requestId
        ? { ...group, expanded: !group.expanded }
        : group
    ));
  };

  const expandCollapseAll = () => {
    if (activeTab !== 'trader') {
      return;
    }

    const allExpanded = traderLogGroups.every(g => g.expanded);
    setTraderLogGroups(prev => prev.map(group => ({ ...group, expanded: !allExpanded })));
    setTraderFilteredGroups(prev => prev.map(group => ({ ...group, expanded: !allExpanded })));
  };

  const downloadLogs = () => {
    if (activeTab !== 'trader') {
      if (executionDetail?.events?.length) {
        const content = executionDetail.events
          .map((event) => `[${event.timestamp ?? event.eventTimestamp ?? ''}] [${event.type ?? 'Event'}] ${JSON.stringify(event)}`)
          .join('\n');
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `workflow-execution-${new Date().toISOString()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      return;
    }

    const content = traderFilteredGroups.flatMap(group =>
      group.logs.map(log =>
        `[${new Date(log.timestamp).toISOString()}] [${group.requestId}] ${log.message}`
      )
    ).join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trading-api-logs-${new Date().toISOString()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getExecutionDisplayName = (execution: ExecutionSummary): string => {
    if (execution.name) {
      return execution.name;
    }
    const arnSuffix = execution.executionArn.split(':').pop();
    return arnSuffix ?? execution.executionArn;
  };

  const formatExecutionDate = (value?: string) => {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString();
  };

  const getExecutionStatusClass = (status?: string) => {
    switch (status) {
      case 'SUCCEEDED':
        return 'bg-green-100 text-green-700';
      case 'FAILED':
      case 'TIMED_OUT':
      case 'ABORTED':
        return 'bg-red-100 text-red-700';
      case 'RUNNING':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-200 text-gray-700';
    }
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const highlightSearchTerm = (text: string) => {
    if (!searchTerm) return text;

    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === searchTerm.toLowerCase()
        ? <mark key={index} className="bg-yellow-200">{part}</mark>
        : part
    );
  };

  const getLogLevelColor = (message: string) => {
    if (message.includes('ERROR') || message.includes('❌')) return 'text-red-600';
    if (message.includes('WARN')) return 'text-yellow-600';
    if (message.includes('✅')) return 'text-green-600';
    if (message.includes('===')) return 'text-blue-600 font-semibold';
    return 'text-gray-700';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg w-full flex flex-col min-h-[80vh]">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Debug Logs</h2>
            {activeTab === 'trader' && traderMetadata && (
              <div className="text-sm text-gray-500 mt-1">
                <p>Last {traderMetadata.hours || 24} hours • {traderMetadata.count} events • {traderMetadata.uniqueRequests || traderLogGroups.length} unique requests</p>
                <p className="text-xs">Environment: {traderMetadata.environment} • Retrieved {traderMetadata.pagesRetrieved || 1} page(s)</p>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as LogType)} className="flex-1 flex flex-col min-h-0">
          <div className="px-4 pt-4 flex-shrink-0">
            <TabsList className="grid w-full max-w-xl grid-cols-2">
              <TabsTrigger value="trader" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Trading API
              </TabsTrigger>
              <TabsTrigger value="executions" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Step Functions
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Toolbar */}
          <div className="p-4 border-b border-gray-200 flex items-center gap-4 flex-shrink-0">
            {activeTab === 'trader' ? (
              <>
                <div className="flex-1 text-xs text-gray-500">
                  Trading API is used to handle the webhook only, trade execution is handled by Step Functions.
                </div>
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search logs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={expandCollapseAll}
                  disabled={loading || traderLogGroups.length === 0}
                >
                  {traderLogGroups.every(g => g.expanded) ? 'Collapse All' : 'Expand All'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => fetchLogs('trader')}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={downloadLogs}
                  disabled={loading || traderFilteredGroups.length === 0}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-3 w-full">
                <div className="flex-1 text-sm text-gray-600 truncate">
                  Workflow environment: {executionEnvironment ?? 'unknown'}
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => fetchExecutionList({ reset: true })}
                  disabled={executionListLoading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${executionListLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleLoadMoreExecutions}
                  disabled={executionListLoading || !executionListNextToken}
                >
                  Load More
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={downloadLogs}
                  disabled={executionDetailLoading || !(executionDetail?.events?.length)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Events
                </Button>
              </div>
            )}
          </div>

          <TabsContent value="trader" className="flex-1 min-h-0 overflow-y-auto p-4 mt-0">
            {loading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-start">
                <AlertCircle className="h-5 w-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error loading logs</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            )}

            {!loading && !error && traderFilteredGroups.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {searchTerm ? 'No logs match your search' : 'No trading API logs found in the selected window'}
              </div>
            )}

            {!loading && !error && traderFilteredGroups.length > 0 && (
              <div className="space-y-3">
                {traderFilteredGroups.map((group) => (
                  <div
                    key={group.requestId}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Group Header */}
                    <div
                      className={`px-3 py-2 flex items-center justify-between cursor-pointer hover:bg-gray-100 ${group.logs.some(log => log.message.includes('ERROR') || log.message.includes('❌'))
                        ? 'bg-red-50'
                        : 'bg-gray-50'
                        }`}
                      onClick={() => toggleGroup(group.requestId)}
                    >
                      <div className="flex items-center gap-2">
                        {group.expanded ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        )}
                        <span className="font-mono text-xs font-medium text-gray-700">
                          {group.requestId === 'ungrouped' ? 'Ungrouped Logs' : `Request: ${group.requestId}`}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({group.logs.length} logs)
                        </span>
                        {group.logs.some(log => log.message.includes('ERROR') || log.message.includes('❌')) && (
                          <span className="text-xs text-red-600 font-semibold">ERROR</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatTimestamp(group.startTime)}
                        {group.startTime !== group.endTime && (
                          <> - {formatTimestamp(group.endTime)}</>
                        )}
                      </div>
                    </div>

                    {/* Group Logs */}
                    {group.expanded && (
                      <div className="bg-white p-2 space-y-1">
                        {group.logs.map((log, index) => (
                          <div
                            key={`${group.requestId}-${index}`}
                            className="font-mono text-[10px] leading-relaxed py-1 hover:bg-gray-50 px-2 rounded"
                          >
                            <div className="flex items-start gap-2">
                              <span className="text-gray-400 flex-shrink-0 text-[10px]">
                                {new Date(log.timestamp).toLocaleTimeString()}
                              </span>
                              <pre className={`whitespace-pre-wrap break-all flex-1 ${getLogLevelColor(log.message)} text-[10px]`}>
                                {highlightSearchTerm(log.message)}
                              </pre>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="executions" className="flex flex-1 min-h-0 flex-col overflow-hidden p-4 mt-0">
            <div className="flex-shrink-0 space-y-3">
              {executionListError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Failed to load executions</h3>
                    <p className="text-sm text-red-700 mt-1">{executionListError}</p>
                  </div>
                </div>
              )}

              {executionDetailError && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-yellow-800">Execution details unavailable</h3>
                    <p className="text-sm text-yellow-700 mt-1">{executionDetailError}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-1 min-h-0 overflow-hidden gap-4 pt-4 md:pt-0 md:flex-row flex-col">
              <div className="md:w-72 w-full md:flex-none border border-gray-200 rounded-lg bg-white shadow-sm flex flex-col min-h-0 max-h-full overflow-hidden">
                <div className="px-4 py-2 border-b border-gray-200 text-sm font-semibold text-gray-800">
                  Executions
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-2 scrollbar-hide">
                  {executionListLoading && executionList.length === 0 && (
                    <div className="flex justify-center py-6">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                    </div>
                  )}

                  {!executionListLoading && executionList.length === 0 && (
                    <div className="text-xs text-gray-500 text-center py-6 px-2">
                      No executions found for this workflow yet.
                    </div>
                  )}

                  {executionList.map((execution) => {
                    const isSelected = execution.executionArn === selectedExecutionArn;
                    return (
                      <button
                        key={execution.executionArn}
                        type="button"
                        onClick={() => handleSelectExecution(execution.executionArn)}
                        className={`w-full text-left rounded-md border px-3 py-2 text-xs transition shadow-sm ${isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700'
                          }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono truncate">{getExecutionDisplayName(execution)}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide ${getExecutionStatusClass(execution.status)}`}>
                            {execution.status ?? 'UNKNOWN'}
                          </span>
                        </div>
                        <div className="mt-2 space-y-1 text-[10px] text-gray-600">
                          <div>Started: {formatExecutionDate(execution.startDate)}</div>
                          <div>Stopped: {formatExecutionDate(execution.stopDate)}</div>
                        </div>
                      </button>
                    );
                  })}

                  {executionListNextToken && (
                    <div className="text-xs text-gray-500 text-center pt-2">
                      More executions available. Use <span className="font-semibold">Load More</span> above to fetch the next page.
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden max-h-full">
                <div className="px-4 py-2 border-b border-gray-200 text-sm font-semibold text-gray-800">
                  Execution Details
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
                  {executionDetailLoading && !executionDetail && (
                    <div className="flex h-full items-center justify-center px-4 py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    </div>
                  )}

                  {executionDetail && executionDetailLoading && (
                    <div className="px-4 pt-3 text-xs text-gray-500">Refreshing execution data…</div>
                  )}

                  {!executionDetailLoading && !executionDetail && (
                    <div className="flex h-full items-center justify-center px-4 py-8 text-sm text-gray-500 text-center">
                      {selectedExecutionArn
                        ? 'Execution details are loading.'
                        : 'Select an execution to browse its events.'}
                    </div>
                  )}

                  {executionDetail && (
                    <div className="p-4 space-y-4">
                      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                        <h3 className="text-sm font-semibold text-gray-800 mb-2">Execution Summary</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-600">
                          <div><span className="font-medium text-gray-700">Status:</span> {executionDetail.execution?.status ?? 'unknown'}</div>
                          <div><span className="font-medium text-gray-700">Execution ARN:</span> <code className="break-all">{executionDetail.execution?.executionArn}</code></div>
                          <div><span className="font-medium text-gray-700">Started:</span> {formatDateTime(executionDetail.execution?.startDate)}</div>
                          <div><span className="font-medium text-gray-700">Stopped:</span> {executionDetail.execution?.stopDate ? formatDateTime(executionDetail.execution.stopDate) : 'running'}</div>
                          {executionDetail.execution?.input && (
                            <details className="md:col-span-2">
                              <summary className="flex cursor-pointer items-center justify-between rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700">
                                <span>Input</span>
                                <span className="text-[10px] text-gray-500">Click to expand</span>
                              </summary>
                              <pre className="mt-2 bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-wrap break-words text-[11px]">
                                {formatStructuredText(executionDetail.execution.input, safeParseJson(executionDetail.execution.input))}
                              </pre>
                            </details>
                          )}
                          {executionDetail.execution?.output && (
                            <details className="md:col-span-2">
                              <summary className="flex cursor-pointer items-center justify-between rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700">
                                <span>Output</span>
                                <span className="text-[10px] text-gray-500">Click to expand</span>
                              </summary>
                              <pre className="mt-2 bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-wrap break-words text-[11px]">
                                {formatStructuredText(executionDetail.execution.output, safeParseJson(executionDetail.execution.output))}
                              </pre>
                            </details>
                          )}
                      </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                      <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-800">Step Timeline</h3>
                        <span className="text-xs text-gray-500">{stateSummaries.length} steps</span>
                      </div>
                      {stateSummaries.length > 0 ? (
                        <div className="text-xs">
                          <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4 px-4 py-2 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                            <span>Step</span>
                            <span>Status</span>
                            <span className="justify-self-end">Timestamp</span>
                          </div>
                          <div className="divide-y divide-gray-100">
                          {stateSummaries.map((summary) => {
                            const isExpanded = expandedSummaries[summary.id] ?? false;
                            const inputText = formatStructuredText(summary.inputRaw, summary.inputJson);
                            const outputText = formatStructuredText(summary.outputRaw, summary.outputJson);
                            const timestampLabel = summary.endedAt ?? summary.startedAt;
                            const summaryLogGroups = deriveSummaryLogGroups(summary);
                            const lambdaSections = summaryLogGroups.map((group) => ({
                              group,
                              logs: filterLogsForSummaryWindow(lambdaLogsByGroup[group] ?? [], summary),
                            }));

                            return (
                              <div key={summary.id} className="bg-white">
                                  <button
                                    type="button"
                                    onClick={() => toggleSummary(summary.id)}
                                    className="w-full grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4 px-4 py-3 text-left hover:bg-gray-50 transition"
                                  >
                                    <span className="flex items-center gap-2 font-semibold text-gray-800">
                                      {isExpanded ? (
                                        <ChevronDown className="h-4 w-4 text-gray-500" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 text-gray-500" />
                                      )}
                                      {summary.name}
                                    </span>
                                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] ${statusBadgeClass(summary.status)}`}>
                                      {statusLabel(summary.status)}
                                    </span>
                                    <span className="text-gray-600 justify-self-end">
                                      {timestampLabel ? formatDateTime(timestampLabel) : 'n/a'}
                                    </span>
                                  </button>
                                  {isExpanded && (
                                    <div className="px-4 pb-4 text-[11px] text-gray-600 bg-gray-50">
                                      <div className="flex flex-wrap gap-3">
                                        <span>Started: {formatDateTime(summary.startedAt)}</span>
                                        <span>Ended: {summary.endedAt ? formatDateTime(summary.endedAt) : 'n/a'}</span>
                                        <span>Events: {summary.events.length}</span>
                                      </div>
                                      <div className="mt-3 space-y-3 text-gray-700">
                                        {(summary.error || summary.cause) && (
                                          <div className="border border-red-200 bg-red-50 rounded p-3 text-red-700">
                                            <div className="font-semibold text-red-800">Error Details</div>
                                            {summary.error && <div className="mt-1">Error: {summary.error}</div>}
                                            {summary.cause && (
                                              <pre className="mt-2 bg-white border border-red-200 rounded p-2 whitespace-pre-wrap break-words text-[11px]">
                                                {summary.cause}
                                              </pre>
                                            )}
                                          </div>
                                        )}
                                        {inputText && (
                                          <div>
                                            <div className="font-medium text-gray-700 mb-1">Input</div>
                                            <pre className="bg-white border border-gray-200 rounded p-2 whitespace-pre-wrap break-words text-[11px]">
                                              {inputText}
                                            </pre>
                                          </div>
                                        )}
                                        {outputText && (
                                          <div>
                                            <div className="font-medium text-gray-700 mb-1">Output</div>
                                            <pre className="bg-white border border-gray-200 rounded p-2 whitespace-pre-wrap break-words text-[11px]">
                                              {outputText}
                                            </pre>
                                          </div>
                                        )}
                                      <div>
                                        <div className="font-medium text-gray-700 mb-1">Lambda Logs</div>
                                        {workflowLogsLoading ? (
                                          <div className="text-gray-500">Loading workflow logs…</div>
                                        ) : workflowLogsError ? (
                                          <div className="text-red-600">Failed to load workflow logs: {workflowLogsError}</div>
                                        ) : !workflowLogs?.correlationId ? (
                                          <div className="text-gray-500">Workflow logs are unavailable because the execution input did not include a correlation ID.</div>
                                        ) : lambdaSections.length === 0 ? (
                                          <div className="text-gray-500">No Lambda invocations recorded for this step.</div>
                                        ) : (
                                          <div className="space-y-2">
                                            {lambdaSections.map(({ group, logs }) => (
                                              <div key={group} className="border border-gray-200 rounded bg-white">
                                                <div className="px-3 py-2 bg-gray-100 text-gray-700 font-semibold uppercase tracking-wide">
                                                  {group.split('/').pop() ?? group}
                                                </div>
                                                <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                                                  {logs.length > 0 ? (
                                                    logs.map((log, index) => (
                                                      <div key={`${group}-${log.timestamp}-${index}`} className="px-3 py-2 text-gray-700">
                                                        <div className="flex justify-between text-gray-500 mb-1">
                                                          <span>{formatTimestamp(log.timestamp)}</span>
                                                          {log.logStreamName && (
                                                            <span className="truncate ml-2">{log.logStreamName}</span>
                                                          )}
                                                        </div>
                                                        <pre className="whitespace-pre-wrap break-words">{log.message}</pre>
                                                      </div>
                                                    ))
                                                  ) : (
                                                    <div className="px-3 py-2 text-gray-500">No log entries captured for this step&apos;s timeframe.</div>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>

                                      {summary.events.length > 0 && (
                                        <details className="text-gray-600">
                                          <summary className="cursor-pointer text-[11px] font-medium">Raw events</summary>
                                          <div className="mt-2 space-y-2">
                                            {summary.events.map((event, index) => (
                                              <pre
                                                  key={event.id ?? `${summary.id}-event-${index}`}
                                                  className="bg-white border border-gray-200 rounded p-2 whitespace-pre-wrap break-words text-[11px]"
                                                >
                                                  {JSON.stringify(event, null, 2)}
                                                </pre>
                                              ))}
                                            </div>
                                          </details>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="px-4 py-3 text-xs text-gray-500">No steps available for this execution.</div>
                      )}
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                      <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-800">Step Function Logs</h3>
                        {workflowLogs?.stepFunctionLogGroup && (
                          <span className="text-xs text-gray-500 truncate max-w-xs">{workflowLogs.stepFunctionLogGroup}</span>
                        )}
                      </div>
                      {workflowLogsLoading ? (
                        <div className="flex items-center justify-center py-6">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                        </div>
                      ) : workflowLogsError ? (
                        <div className="px-4 py-3 text-xs text-red-600">Failed to load Step Functions logs: {workflowLogsError}</div>
                      ) : stepFunctionLogs.length > 0 ? (
                        <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 text-[11px] font-mono bg-white">
                          {stepFunctionLogs.map((log, index) => (
                            <div key={`${log.logStreamName ?? 'stream'}-${log.timestamp}-${index}`} className="px-4 py-2 text-gray-700">
                              <div className="flex flex-wrap items-baseline gap-2 text-gray-500 mb-1">
                                <span>{formatTimestamp(log.timestamp)}</span>
                                {log.logStreamName && <span className="truncate">{log.logStreamName}</span>}
                              </div>
                              <pre className="whitespace-pre-wrap break-words">{log.message}</pre>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="px-4 py-3 text-xs text-gray-500">
                          No Step Functions log entries were found for this execution. Logging may be disabled for the state machine.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {executionDetail?.nextToken && (
                  <div className="border-t border-gray-200 p-3 bg-gray-50 flex justify-center">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleLoadMoreEvents}
                      disabled={executionDetailLoading}
                    >
                      {executionDetailLoading ? 'Loading events…' : 'Load more events'}
                    </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
        </TabsContent>
      </Tabs>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
        <div className="text-sm text-gray-500">
          {activeTab === 'trader'
            ? `${traderFilteredGroups.length} groups, ${traderFilteredGroups.reduce((sum, g) => sum + g.logs.length, 0)} of ${traderLogs.length} logs shown`
            : executionDetail?.events
              ? `${executionDetail.events.length} workflow events loaded`
              : executionList.length > 0
                ? 'Select an execution to view workflow events'
                : 'No executions loaded'}
        </div>
      </div>
    </div>
  );
};
